import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  Tooltip,
  Stack
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from '../../providers/SnackBarProvider';
import { useAppBar } from '../../providers/AppBarProvider';
import axios from 'axios';
import { handleError } from '../../utils/lib';
import { useTranslation } from 'react-i18next';
import VetActionsBottomSheet from '../../layouts/VetActionsBottomSheet';

const ViewVetDeposits = () => {
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const { updateTitle } = useAppBar();
  const [deposits, setDeposits] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [idToDelete, setIdToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { t } = useTranslation();

  useEffect(() => {
    updateTitle('', t('view.depositsTitle'), t('view.depositsSubtitle'));
    fetchDeposits();
  }, [t]);

  const fetchDeposits = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}vets?deposits`,
        { withCredentials: true }
      );
      setDeposits(response.data.data || []);
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = (item) => {
    setIdToDelete(item.id);
    setDeleteDialogOpen(true);
  };

  const handleEdit = (item) => {
    navigate('/main/edit-vet-deposit', { state: { updateDeposit: item } });
  };

  const handleCreate = () => {
    navigate('/main/edit-vet-deposit');
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      await axios.delete(
        `${import.meta.env.VITE_API_URL}vets?delete_deposit&id=${idToDelete}`,
        { withCredentials: true }
      );
      setDeposits(deposits.filter(s => s.id !== idToDelete));
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setIdToDelete(null);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl">
      <Paper sx={{ p: 2 }}>
        <List>
          {deposits.map((deposit, index) => (
            <React.Fragment key={deposit.id}>
              <ListItem
                secondaryAction={
                  <Box>
                    <Tooltip title={t('view.editDeposit')}>
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => handleEdit(deposit)}
                        color="default"
                        sx={{ mr: 1 }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('view.deleteDeposit')}>
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => handleDelete(deposit)}
                        color="default"
                        disabled={loading}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                }
                sx={{ paddingRight: "144px" }}
              >
                <ListItemText
                  primary={deposit.name}
                  secondary={deposit.is_central ? t('view.depositCentralBadge') : ''}
                />
              </ListItem>
              {index < deposits.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t('common.confirmDeletion')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t('view.confirmDeleteDeposit')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button onClick={confirmDelete} color="error" autoFocus disabled={loading}>
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Action Buttons */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreate}
            startIcon={<AddIcon />}
          >
            {t('view.createDeposit')}
          </Button>
        </Stack>
      </Box>

      <VetActionsBottomSheet />
    </Container>
  );
};

export default ViewVetDeposits; 